/**
 * Pure drag-reorder kernels for the sidebar — extracted verbatim
 * (behavior-preserving) from the inline `handleDragOver` / `handleModuleDragOver`
 * splice logic and the `sameOrderById` reconcile check in layout/Sidebar.tsx.
 *
 * These are the algorithmic cores that the stateful `useDragReorder` /
 * `useModuleDragReorder` hooks delegate to. Keeping them pure lets them be
 * characterization-tested directly (no DOM, no React) and guarantees the
 * extracted hooks compute byte-identical orders to the original inline code.
 */

/**
 * Move the element currently at `fromIndex` to `toIndex`, returning a NEW array.
 * Mirrors the inline `const newOrder = [...items]; const [moved] =
 * newOrder.splice(from, 1); newOrder.splice(to, 0, moved);` used by both
 * `handleDragOver` (conversations) and `handleModuleDragOver` (modules).
 */
export function reorderByIndex<T>(items: readonly T[], fromIndex: number, toIndex: number): T[] {
  const newOrder = [...items];
  const [moved] = newOrder.splice(fromIndex, 1);
  newOrder.splice(toIndex, 0, moved);
  return newOrder;
}

/**
 * True when two conversation/module arrays carry the SAME ids in the SAME order.
 * Verbatim port of the inline `sameOrderById` useCallback in Sidebar.tsx — used
 * to clear the temporary drag order only once the query cache has caught up to
 * the committed order.
 */
export function sameOrderById<T extends { id: string }>(a: readonly T[], b: readonly T[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id) return false;
  }
  return true;
}

/**
 * Compute the persisted module position updates after a module drag ends.
 * Verbatim port of the inline `handleModuleDragEnd` body: map each module to its
 * new index, then keep ONLY those whose stored `position` differs from the new
 * index. The caller fires one `updateWorkspaceModule({ position })` per entry.
 */
export function computeModulePositionUpdates<T extends { module: { id: string; position?: number } }>(
  tempModuleOrder: readonly T[],
): Array<{ mod: T; newPos: number }> {
  return tempModuleOrder
    .map((mod, i) => ({ mod, newPos: i }))
    .filter(({ mod, newPos }) => mod.module.position !== newPos);
}
